/**
 * MAESPACE — «Apri in MaeSpace» e la stanza della commessa (F1).
 *
 * Contratto: `maefwk10/docs/contract/BE-DONE-MAESPACE-F1-v1.md` e la risposta alla nostra
 * controproposta, `BE-RESPONSE-TO-FE-MAESPACE-CONTROPROPOSTA-v1.md` (BE v1.244.0). Sono endpoint
 * `plain` sotto il cancello dei moduli: modulo `maespace` spento → `403 MODULE_DISABLED`.
 *
 * ⚠️ I nomi qui sotto sono quelli del filo (il controller li scrive in inglese) e
 * `tests/unit/maespaceWireNames.test.ts` li tiene fermi: in una risposta `plain` un campo che
 * cambia nome diventa `undefined`, non un errore.
 */
import { useQuery } from '@tanstack/react-query';
import { httpRequest } from '@/shared/api/client';

/** `none` = nessuna stanza (il perché sta in `reason`); `pending` = in creazione o da riprovare. */
export type RoomStatus = 'none' | 'pending' | 'active' | 'failed';

/**
 * Perché una commessa non ha la stanza; valorizzato solo con `status: 'none'`.
 * - `tenant_off`: MaeSpace non è attivo per l'azienda (modulo o piattaforma);
 * - `not_allowlisted`: siamo in prova, solo le commesse `ZZTEST-…` hanno una stanza;
 * - `not_created`: ne ha diritto e arriva al prossimo allineamento (circa un minuto).
 */
export type RoomReason = 'tenant_off' | 'not_allowlisted' | 'not_created';

export interface RoomMember {
  email: string;
  userId: string | null;
  /**
   * `true` se è sicuramente utente MaeSpace, `null` se non si sa. MaeSpace non lo dice: il BE lo
   * deduce solo da una stanza **non** pubblica (un esterno l'avrebbe resa pubblica), quindi su una
   * stanza pubblica è `null` per tutti. Non è mai `false`.
   */
  inMaeSpace: boolean | null;
}

export interface ProjectRoom {
  projectId: string;
  enabled: boolean;
  status: RoomStatus;
  reason: RoomReason | null;
  cuid: string | null;
  url: string | null;
  name: string | null;
  /** Fra i membri c'è chi non è ancora utente MaeSpace: la call accetta richieste dalla sala d'attesa. */
  isPublic: boolean;
  lastSyncedAt: string | null;
  syncError: string | null;
  /** Solo dalla lettura della singola commessa; `null` nella lettura a lotti. */
  members: RoomMember[] | null;
}

export interface SsoLink {
  redirectUrl: string;
  expiresInSeconds: number;
}

/**
 * Il link di accesso: **monouso e valido 60 secondi**, quindi si chiede al clic e mai prima.
 * Niente nuovi tentativi automatici: ogni richiesta annulla il link precedente.
 */
export async function requestSsoLink(): Promise<SsoLink> {
  const res = await httpRequest<{ data: SsoLink }>('/api/maespace/sso-link', {
    method: 'POST', endpointType: 'plain', retries: 0,
  });
  return res.data;
}

export async function fetchProjectRoom(projectId: string): Promise<ProjectRoom> {
  const res = await httpRequest<{ data: ProjectRoom }>(
    `/api/maespace/projects/${encodeURIComponent(projectId)}/room`, { endpointType: 'plain' },
  );
  return res.data;
}

/**
 * L'**archivio** di una stanza in MaeSpace, invece della call.
 *
 * Il BE dà solo `url`, che è `…/videochat?room=<cuid>`: apre la videochiamata. L'archivio dei documenti
 * è un'altra pagina del frontend MaeSpace, `…/repository?cuid=<cuid>` — è il percorso che MaeSpace stesso
 * usa (`toRoomRepo` nel suo bundle, letto il 09-10-2026: nella guida tecnica non c'è). Si compone
 * dall'origine di `url`, così segue il dominio vero (prod o dev) senza scriverlo qui.
 * `null` se manca un pezzo: chi chiama ripiega su `url`.
 */
export function roomRepositoryUrl(room: { url: string | null; cuid: string | null } | null | undefined): string | null {
  if (!room?.url || !room.cuid) return null;
  const origin = /^https?:\/\/[^/?#]+/i.exec(room.url)?.[0];
  return origin ? `${origin}/repository?cuid=${encodeURIComponent(room.cuid)}` : null;
}

/** Il tetto del BE sulla lettura a lotti. */
export const ROOMS_BATCH_MAX = 200;

/** Le stanze di più commesse; quelle inesistenti o non visibili sono semplicemente assenti. */
export async function fetchRooms(projectIds: readonly string[]): Promise<ProjectRoom[]> {
  if (projectIds.length === 0) return [];
  const out: ProjectRoom[] = [];
  for (let i = 0; i < projectIds.length; i += ROOMS_BATCH_MAX) {
    const slice = projectIds.slice(i, i + ROOMS_BATCH_MAX);
    const qs = new URLSearchParams({ projectIds: slice.join(',') });
    const res = await httpRequest<{ data: ProjectRoom[] }>(`/api/maespace/rooms?${qs.toString()}`, { endpointType: 'plain' });
    out.push(...(res.data ?? []));
  }
  return out;
}

export const maespaceKeys = {
  room: (projectId: string) => ['maespace', 'room', projectId] as const,
  rooms: (projectIds: readonly string[]) => ['maespace', 'rooms', [...projectIds].sort().join(',')] as const,
};

/**
 * La stanza di una commessa. Mentre è `pending` si rilegge ogni 20 secondi: il BE la allinea entro circa un minuto, e chi guarda
 * la scheda Team non deve ricaricare la pagina per vederla comparire.
 */
export function useProjectRoom(projectId: string, enabled: boolean) {
  return useQuery({
    queryKey: maespaceKeys.room(projectId),
    queryFn: () => fetchProjectRoom(projectId),
    enabled: enabled && Boolean(projectId),
    refetchInterval: (q) => (isSettling(q.state.data) ? 20_000 : false),
  });
}

/**
 * La stanza è in arrivo: vale la pena rileggerla. Solo `pending`: `not_created` non arriva da sola
 * (vedi `roomExplanation`), e rileggerla ogni 20 secondi vorrebbe dire farlo per sempre.
 */
export function isSettling(room: ProjectRoom | undefined): boolean {
  if (!room?.enabled) return false;
  return room.status === 'pending';
}

/** Le stanze delle commesse a vista, in una chiamata (elenco commesse). */
export function useRooms(projectIds: readonly string[], enabled: boolean) {
  return useQuery({
    queryKey: maespaceKeys.rooms(projectIds),
    queryFn: () => fetchRooms(projectIds),
    enabled: enabled && projectIds.length > 0,
    staleTime: 60_000,
  });
}

export interface SyncResult {
  outcome: 'created' | 'updated' | 'unchanged' | 'skipped';
  detail: string | null;
  cuid: string | null;
  url: string | null;
}

/** Allinea subito la stanza, fuori coda. Solo super-admin (il BE lo verifica). */
export async function syncProjectRoom(projectId: string): Promise<SyncResult> {
  const res = await httpRequest<{ data: SyncResult }>(
    `/api/maespace/projects/${encodeURIComponent(projectId)}/sync`, { method: 'POST', endpointType: 'plain', retries: 0 },
  );
  return res.data;
}

// ═══════════════════ F3 — i documenti della stanza della commessa ═══════════════════
// Contratto: `maefwk10/docs/contract/BE-TO-FE-MAESPACE-IMPLEMENTAZIONE-v1.md` §3. Li vedono solo i
// membri della stanza (e il super-admin): agli altri `403 MAESPACE_NOT_A_MEMBER`.

export interface RoomFolder {
  name: string;
  /** Percorso completo, separatore `/`. */
  path: string;
  /** Cartella di una riunione: niente rinomina né cancellazione. */
  isProtected: boolean;
}

export interface RoomFile {
  fileId: string;
  name: string;
  /** `""` = radice. */
  folderPath: string;
  size: number | null;
  uploadedBy: string | null;
  uploadedAt: string | null;
}

export interface ProjectFiles {
  projectId: string;
  roomCuid: string;
  url: string;
  folders: RoomFolder[];
  files: RoomFile[];
}

/** Un link firmato per scaricare: vale 15 minuti, si chiede al clic. */
export interface DownloadLink {
  url: string;
  name: string;
  expiresInSeconds: number;
}

const projectBase = (projectId: string) => `/api/maespace/projects/${encodeURIComponent(projectId)}`;

export async function fetchProjectFiles(projectId: string): Promise<ProjectFiles> {
  const res = await httpRequest<{ data: ProjectFiles }>(`${projectBase(projectId)}/files`, { endpointType: 'plain' });
  return res.data;
}

export async function requestProjectFileDownload(projectId: string, fileId: string): Promise<DownloadLink> {
  const res = await httpRequest<{ data: DownloadLink }>(
    `${projectBase(projectId)}/files/${encodeURIComponent(fileId)}/download`, { endpointType: 'plain', retries: 0 },
  );
  return res.data;
}

/** Il tetto del BE per un caricamento (413 `MAESPACE_UPLOAD_TOO_LARGE` oltre). */
export const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;

export interface UploadedFile { fileId: string; name: string; folderPath: string }

export async function uploadProjectFile(projectId: string, file: File, folderPath?: string | null): Promise<UploadedFile> {
  const form = new FormData();
  form.append('file', file, file.name);
  if (folderPath) form.append('folderPath', folderPath);
  const res = await httpRequest<{ data: UploadedFile }>(
    `${projectBase(projectId)}/files`, { method: 'POST', body: form, endpointType: 'plain', retries: 0 },
  );
  return res.data;
}

/** Definitivo, anche in MaeSpace: chi chiama chiede conferma prima. */
export async function deleteProjectFile(projectId: string, fileId: string): Promise<void> {
  await httpRequest(`${projectBase(projectId)}/files/${encodeURIComponent(fileId)}`, {
    method: 'DELETE', endpointType: 'plain', retries: 0,
  });
}

/** Crea la cartella, e i genitori che mancano. */
export async function createProjectFolder(projectId: string, path: string): Promise<{ path: string }> {
  const res = await httpRequest<{ data: { path: string } }>(`${projectBase(projectId)}/folders`, {
    method: 'POST', body: { path }, endpointType: 'plain', retries: 0,
  });
  return res.data;
}

export interface PublishToPortalInput { title?: string; description?: string; category?: string }
export interface PublishToPortalResult { clientPortalDocumentId: string; mediaFileId: string; title: string; clientId: string }

/**
 * Pubblica un file della stanza nel portale **del cliente della commessa** (F4). Mai automatico: è
 * un gesto del PM, file per file. Serve `client.portal.manage` (o super-admin) **e** essere nella stanza.
 */
export async function publishFileToPortal(projectId: string, fileId: string, input: PublishToPortalInput = {}): Promise<PublishToPortalResult> {
  const res = await httpRequest<{ data: PublishToPortalResult }>(
    `${projectBase(projectId)}/files/${encodeURIComponent(fileId)}/publish-to-portal`,
    { method: 'POST', body: input, endpointType: 'plain', retries: 0 },
  );
  return res.data;
}

// ═══════════════════ F2/F3 — gli output di una riunione del planner ═══════════════════

export interface MeetingFiles {
  planningEntryId: string;
  roomCuid: string | null;
  meetingCuid: string | null;
  url: string | null;
  /** `null` finché la riunione non si è tenuta. */
  folder: string | null;
  /** Il registro presenze: arriva circa un minuto dopo la fine. */
  registerFileId: string | null;
  files: RoomFile[];
}

const entryBase = (entryId: string) => `/api/maespace/planning-entries/${encodeURIComponent(entryId)}`;

export async function fetchMeetingFiles(entryId: string): Promise<MeetingFiles> {
  const res = await httpRequest<{ data: MeetingFiles }>(`${entryBase(entryId)}/files`, { endpointType: 'plain' });
  return res.data;
}

export async function requestMeetingFileDownload(entryId: string, fileId: string): Promise<DownloadLink> {
  const res = await httpRequest<{ data: DownloadLink }>(
    `${entryBase(entryId)}/files/${encodeURIComponent(fileId)}/download`, { endpointType: 'plain', retries: 0 },
  );
  return res.data;
}

// ═══════════════════ Trasversale — le mie stanze ═══════════════════

export type RoomOwnerType = 'project' | 'task' | 'planning-entry' | 'meeting-room-booking';

export interface MyRoom {
  ownerType: RoomOwnerType;
  ownerId: string;
  name: string | null;
  cuid: string;
  url: string;
  isPublic: boolean;
  lastSyncedAt: string | null;
  /** Solo per le stanze di commessa. */
  project: { id: string; code: string | null; name: string | null } | null;
}

/** Le stanze attive in cui c'è l'utente, le più recenti prima (massimo 200). */
export async function fetchMyRooms(): Promise<MyRoom[]> {
  const res = await httpRequest<{ data: MyRoom[] }>('/api/maespace/my-rooms', { endpointType: 'plain' });
  return res.data ?? [];
}

// ═══════════════════ Amministrazione (solo super-admin) ═══════════════════

export type OutboxStatus = 'queued' | 'sending' | 'sent' | 'failed' | 'dead';

export interface MaeSpaceAdminStatus {
  settings: {
    enabled: boolean;
    /** `false` = nessuna chiave: il server usa un finto. */
    connected: boolean;
    requireAllowlist: boolean;
    testPrefix: string;
    sendEmail: boolean;
    archiveEnabled: boolean;
    nightlyReconcile: boolean;
  };
  queue: { operation: string; status: OutboxStatus; count: number }[];
  errors: {
    id: string; operation: string; entityType: string | null; entityId: string | null; attempts: number;
    nextAttemptAt: string | null; dead: boolean; code: string | null; message: string | null; updatedAt: string | null;
  }[];
  rooms: { ownerType: RoomOwnerType; status: RoomStatus; count: number }[];
  failedRooms: {
    id: string; ownerType: RoomOwnerType; ownerId: string; name: string | null; status: RoomStatus;
    syncError: string | null; updatedAt: string | null;
  }[];
  archivedFiles: number;
}

export async function fetchAdminStatus(): Promise<MaeSpaceAdminStatus> {
  const res = await httpRequest<{ data: MaeSpaceAdminStatus }>('/api/maespace/admin/status', { endpointType: 'plain' });
  return res.data;
}

export async function retryOutbox(id: string): Promise<{ id: string; status: OutboxStatus }> {
  const res = await httpRequest<{ data: { id: string; status: OutboxStatus } }>(
    `/api/maespace/admin/outbox/${encodeURIComponent(id)}/retry`, { method: 'POST', endpointType: 'plain', retries: 0 },
  );
  return res.data;
}

/** «Copia i file esistenti» di un'azienda nell'archivio (409 `MAESPACE_ARCHIVE_DISABLED` se spento). */
export async function backfillArchive(tenantId: string, max?: number): Promise<{ tenantId: string; queued: number }> {
  const qs = new URLSearchParams({ tenantId });
  if (max) qs.set('max', String(max));
  const res = await httpRequest<{ data: { tenantId: string; queued: number } }>(
    `/api/maespace/admin/archive/backfill?${qs.toString()}`, { method: 'POST', endpointType: 'plain', retries: 0 },
  );
  return res.data;
}

export const maespaceQueryKeys = {
  projectFiles: (projectId: string) => ['maespace', 'project-files', projectId] as const,
  meetingFiles: (entryId: string) => ['maespace', 'meeting-files', entryId] as const,
  myRooms: () => ['maespace', 'my-rooms'] as const,
  adminStatus: () => ['maespace', 'admin-status'] as const,
};

